// src/components/profile/ProfilePictureUpload.jsx
import React, { useState, useRef } from 'react';
import { Camera, Upload, X, Check } from 'lucide-react';

const ProfilePictureUpload = ({ currentImage, onImageChange }) => {
  const [imagePreview, setImagePreview] = useState(currentImage);
  const [isUploading, setIsUploading] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        alert('File size must be less than 5MB');
        return;
      }

      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        alert('Please upload a valid image file (JPEG, PNG, GIF)');
        return;
      }

      setIsUploading(true);
      
      // Simulate upload delay
      setTimeout(() => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const newImage = reader.result;
          setImagePreview(newImage);
          onImageChange(newImage);
          setIsUploading(false);
        };
        reader.readAsDataURL(file);
      }, 1000);
    }
  };

  const handleRemoveImage = () => {
    setImagePreview('https://api.dicebear.com/7.x/avataaars/svg?seed=default');
    onImageChange(null);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Profile Picture</h3>
          <p className="text-sm text-gray-500">JPG, PNG or GIF. Max size 5MB.</p>
        </div>
        {imagePreview !== currentImage && (
          <button
            onClick={handleRemoveImage}
            className="text-red-600 hover:text-red-700 text-sm font-medium flex items-center"
          >
            <X className="h-4 w-4 mr-1" />
            Reset
          </button>
        )}
      </div>

      <div className="flex flex-col items-center space-y-6">
        {/* Profile Image Preview */}
        <div 
          className="relative group"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div className="h-48 w-48 rounded-full overflow-hidden border-4 border-white shadow-lg relative">
            <img 
              src={imagePreview} 
              alt="Profile" 
              className="h-full w-full object-cover"
            />
            
            {/* Upload Overlay */}
            {(isHovered || isUploading) && (
              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                {isUploading ? (
                  <div className="text-white text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
                    <p className="text-sm">Uploading...</p>
                  </div>
                ) : (
                  <div className="text-white text-center">
                    <Camera className="h-8 w-8 mx-auto mb-2" />
                    <p className="text-sm">Change Photo</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Status Indicator */}
          {imagePreview !== currentImage && (
            <div className="absolute -bottom-2 -right-2 bg-green-500 text-white p-2 rounded-full shadow-lg">
              <Check className="h-4 w-4" />
            </div>
          )}
        </div>

        {/* Upload Controls */}
        <div className="flex flex-col sm:flex-row gap-3 w-full max-w-xs">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="image/*"
            className="hidden"
            id="profile-upload"
          />
          
          <button
            onClick={() => fileInputRef.current.click()}
            disabled={isUploading}
            className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            <Upload className="h-5 w-5" />
            <span>{isUploading ? 'Uploading...' : 'Upload New'}</span>
          </button>

          <button
            onClick={() => {
              // Use webcam functionality would go here
              alert('Camera access would be requested here');
            }}
            className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center justify-center space-x-2"
          >
            <Camera className="h-5 w-5" />
            <span>Take Photo</span>
          </button>
        </div>

        {/* Image Guidelines */}
        <div className="text-center text-sm text-gray-500">
          <p>For best results, use a square image at least 400x400 pixels.</p>
          <p>Make sure your face is clearly visible.</p>
        </div>
      </div>
    </div>
  );
};

export default ProfilePictureUpload;